/**
 * The token of an invitation link (`/acceso/activar#t=<token>`, docs/authentication.md). It travels
 * in the fragment so it never reaches a server log or a `Referer`; this module takes it out of the
 * address bar (and of the history entry) as soon as the page loads and keeps it in memory only —
 * never in storage. A reload loses it on purpose: the person opens the link again.
 */
export const ACTIVATION_PATH = '/acceso/activar';

let token: string | null = null;
const listeners = new Set<() => void>();
let listening = false;

function take(): boolean {
  if (typeof window === 'undefined') return false;
  const { pathname, search, hash } = window.location;
  if (pathname !== ACTIVATION_PATH || !hash) return false;
  const found = new URLSearchParams(hash.slice(1)).get('t');
  if (!found) return false;
  // same history entry and router state, without the fragment
  window.history.replaceState(window.history.state, '', `${pathname}${search}`);
  token = found;
  return true;
}

/**
 * Reads the token of the link and removes it from the address bar. Each app calls it first thing,
 * before rendering (so the router never sees the fragment). Safe to call again: it answers what is
 * in memory. Another link pasted in the same tab (only the fragment changes, no reload) is taken too.
 */
export function captureActivationToken(): string | null {
  take();
  if (!listening && typeof window !== 'undefined') {
    listening = true;
    window.addEventListener('hashchange', () => {
      if (take()) listeners.forEach((l) => l());
    });
  }
  return token;
}

/** The activation ended (done, abandoned): nothing is kept. */
export function clearActivationToken(): void {
  if (token === null) return;
  token = null;
  listeners.forEach((l) => l());
}

export function subscribeActivationToken(fn: () => void): () => void {
  listeners.add(fn);
  return () => void listeners.delete(fn);
}
